import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Mail, MapPin, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';

const content = {
  en: {
    title: "Terms & Conditions",
    lastUpdated: "Last Updated: April 2026",
    intro: {
      title: "Introduction",
      text: "Welcome to WeDo HR Services UG (\"WeDo\", \"we\", \"us\", or \"our\"). These Terms and Conditions govern your use of our website (www.wedohr.net) and the services we provide. By accessing our website or using our services, you agree to be bound by these terms. Please read them carefully before proceeding."
    },
    company: {
      title: "About Us",
      text: "WeDo HR Services UG is a German-registered company providing human resources services including recruitment, corporate training, HR consulting, and career advisory services. Our registered office is located in Düsseldorf, Germany."
    },
    services: {
      title: "Our Services",
      intro: "WeDo offers the following services:",
      items: [
        {
          name: "Recruitment Services",
          description: "Executive search, talent acquisition, candidate screening, and placement services for client organizations."
        },
        {
          name: "Training & Development",
          description: "Professional development programs, corporate training, workshops, and certification courses."
        },
        {
          name: "HR Consulting",
          description: "Strategic HR advisory, organizational development, compensation design, and HR technology solutions."
        },
        {
          name: "Career Advisory",
          description: "Individual career guidance, CV optimization, interview coaching, and professional development planning."
        }
      ],
      note: "The specific terms of any service engagement will be defined in a separate service agreement or contract between WeDo and the client."
    },
    website: {
      title: "Website Use",
      intro: "By using our website, you agree to:",
      items: [
        "Use the website only for lawful purposes and in accordance with these terms",
        "Not attempt to gain unauthorized access to any part of the website or its systems",
        "Not use the website in any way that could damage, disable, or impair our services",
        "Not use automated systems or software to extract data from the website",
        "Provide accurate and complete information when using forms or creating accounts"
      ],
      disclaimer: "The information provided on our website is for general informational purposes only. While we strive to keep the content accurate and up-to-date, we make no warranties about the completeness, reliability, or suitability of this information."
    },
    inquiries: {
      title: "Service Inquiries & Form Submissions",
      text: "Submitting an inquiry, application, or request through our website does not constitute a binding contract or guarantee of service. All inquiries will be reviewed, and we will contact you to discuss your requirements and any potential engagement. A formal service relationship is only established upon execution of a written agreement or confirmation from WeDo."
    },
    recruitment: {
      title: "Recruitment Terms",
      intro: "For candidates using our recruitment services:",
      items: [
        "Your application data will be processed in accordance with our Privacy Policy",
        "We may share your profile with potential employers as part of the recruitment process",
        "You are responsible for ensuring the accuracy of information provided in your application",
        "Placement decisions are made by the hiring organization, not by WeDo",
        "We do not guarantee employment outcomes or specific job placements"
      ],
      clients: "For client organizations using our recruitment services, specific terms including fees, guarantees, and service levels will be defined in your service agreement."
    },
    training: {
      title: "Training & Course Terms",
      intro: "For participants enrolling in our training programs:",
      items: [
        "Enrollment is subject to availability and acceptance by WeDo",
        "Course fees, schedules, and cancellation policies will be communicated at the time of enrollment",
        "Certificates are issued upon successful completion of program requirements",
        "Course materials are provided for personal use only and may not be reproduced or distributed",
        "We reserve the right to modify course content, schedules, or instructors as necessary"
      ]
    },
    ip: {
      title: "Intellectual Property",
      text: "All content on our website, including but not limited to text, graphics, logos, images, and software, is the property of WeDo HR Services UG or its licensors and is protected by German and international intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from our content without prior written permission.",
      trademarks: "The WeDo name, logo, and related marks are trademarks of WeDo HR Services UG. Unauthorized use of these marks is prohibited."
    },
    thirdParty: {
      title: "Third-Party Links",
      text: "Our website may contain links to third-party websites or services that are not owned or controlled by WeDo. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites. Visiting these links is at your own risk."
    },
    liability: {
      title: "Limitation of Liability",
      intro: "To the maximum extent permitted by applicable law:",
      items: [
        "WeDo provides its website and services on an \"as is\" and \"as available\" basis without warranties of any kind",
        "We do not warrant that our website will be uninterrupted, error-free, or free from viruses",
        "WeDo shall not be liable for any indirect, incidental, special, consequential, or punitive damages",
        "Our total liability for any claim shall not exceed the amount paid by you for the specific service giving rise to the claim",
        "These limitations do not apply to liability that cannot be excluded under applicable law"
      ],
      note: "Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by German law."
    },
    indemnification: {
      title: "Indemnification",
      text: "You agree to indemnify and hold harmless WeDo HR Services UG, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of our website or services, your violation of these terms, or your infringement of any third-party rights."
    },
    termination: {
      title: "Termination",
      text: "We reserve the right to terminate or suspend your access to our website or services at any time, without prior notice, for any reason, including breach of these terms. Upon termination, your right to use our website ceases immediately."
    },
    changes: {
      title: "Changes to Terms",
      text: "We may modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on this page. Your continued use of our website after any changes constitutes acceptance of the modified terms. We encourage you to review these terms periodically."
    },
    severability: {
      title: "Severability",
      text: "If any provision of these terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect. The invalid provision will be modified to the minimum extent necessary to make it valid and enforceable."
    },
    governing: {
      title: "Governing Law & Jurisdiction",
      text: "These Terms and Conditions are governed by and construed in accordance with the laws of the Federal Republic of Germany, without regard to conflict of law principles. Any disputes arising from or relating to these terms shall be subject to the exclusive jurisdiction of the courts in Düsseldorf, Germany.",
      consumer: "If you are a consumer within the European Union, you may also have the right to bring proceedings in your country of residence."
    },
    contact: {
      title: "Contact Information",
      intro: "If you have any questions about these Terms and Conditions, please contact us:",
      company: "WeDo HR Services UG",
      address: "Düsseldorf, Germany",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    }
  },
  ar: {
    title: "الشروط والأحكام",
    lastUpdated: "آخر تحديث: أبريل 2026",
    intro: {
      title: "مقدمة",
      text: "مرحباً بكم في ويدو لخدمات الموارد البشرية (\"ويدو\"، \"نحن\"، \"لنا\"). تحكم هذه الشروط والأحكام استخدامك لموقعنا الإلكتروني (www.wedohr.net) والخدمات التي نقدمها. من خلال الوصول إلى موقعنا أو استخدام خدماتنا، فإنك توافق على الالتزام بهذه الشروط. يرجى قراءتها بعناية قبل المتابعة."
    },
    company: {
      title: "من نحن",
      text: "ويدو لخدمات الموارد البشرية هي شركة مسجلة في ألمانيا تقدم خدمات الموارد البشرية بما في ذلك التوظيف والتدريب المؤسسي واستشارات الموارد البشرية والإرشاد المهني. يقع مكتبنا المسجل في دوسلدورف، ألمانيا."
    },
    services: {
      title: "خدماتنا",
      intro: "تقدم ويدو الخدمات التالية:",
      items: [
        {
          name: "خدمات التوظيف",
          description: "البحث التنفيذي واستقطاب المواهب وفحص المرشحين وخدمات التوظيف للمؤسسات العميلة."
        },
        {
          name: "التدريب والتطوير",
          description: "برامج التطوير المهني والتدريب المؤسسي وورش العمل ودورات الشهادات."
        },
        {
          name: "استشارات الموارد البشرية",
          description: "الاستشارات الاستراتيجية للموارد البشرية والتطوير المؤسسي وتصميم التعويضات وحلول تقنية الموارد البشرية."
        },
        {
          name: "الإرشاد المهني",
          description: "التوجيه المهني الفردي وتحسين السيرة الذاتية والتدريب على المقابلات والتخطيط للتطوير المهني."
        }
      ],
      note: "سيتم تحديد الشروط المحددة لأي ارتباط خدمي في اتفاقية خدمة منفصلة أو عقد بين ويدو والعميل."
    },
    website: {
      title: "استخدام الموقع",
      intro: "باستخدامك لموقعنا، فإنك توافق على:",
      items: [
        "استخدام الموقع فقط لأغراض قانونية ووفقاً لهذه الشروط",
        "عدم محاولة الوصول غير المصرح به إلى أي جزء من الموقع أو أنظمته",
        "عدم استخدام الموقع بأي طريقة قد تضر أو تعطل أو تضعف خدماتنا",
        "عدم استخدام أنظمة آلية أو برامج لاستخراج البيانات من الموقع",
        "تقديم معلومات دقيقة وكاملة عند استخدام النماذج أو إنشاء الحسابات"
      ],
      disclaimer: "المعلومات المقدمة على موقعنا هي لأغراض إعلامية عامة فقط. بينما نسعى للحفاظ على دقة المحتوى وتحديثه، لا نقدم أي ضمانات حول اكتمال أو موثوقية أو ملاءمة هذه المعلومات."
    },
    inquiries: {
      title: "استفسارات الخدمة وتقديم النماذج",
      text: "لا يشكل تقديم استفسار أو طلب أو طلب عبر موقعنا عقداً ملزماً أو ضماناً للخدمة. ستتم مراجعة جميع الاستفسارات، وسنتصل بك لمناقشة متطلباتك وأي ارتباط محتمل. تُنشأ علاقة الخدمة الرسمية فقط عند توقيع اتفاقية مكتوبة أو تأكيد من ويدو."
    },
    recruitment: {
      title: "شروط التوظيف",
      intro: "للمرشحين الذين يستخدمون خدمات التوظيف لدينا:",
      items: [
        "ستتم معالجة بيانات طلبك وفقاً لسياسة الخصوصية الخاصة بنا",
        "قد نشارك ملفك الشخصي مع أصحاب العمل المحتملين كجزء من عملية التوظيف",
        "أنت مسؤول عن ضمان دقة المعلومات المقدمة في طلبك",
        "قرارات التوظيف تتخذها المؤسسة الموظفة، وليس ويدو",
        "لا نضمن نتائج التوظيف أو التعيينات الوظيفية المحددة"
      ],
      clients: "للمؤسسات العميلة التي تستخدم خدمات التوظيف لدينا، سيتم تحديد الشروط المحددة بما في ذلك الرسوم والضمانات ومستويات الخدمة في اتفاقية الخدمة الخاصة بك."
    },
    training: {
      title: "شروط التدريب والدورات",
      intro: "للمشاركين المسجلين في برامجنا التدريبية:",
      items: [
        "يخضع التسجيل للتوفر والقبول من قبل ويدو",
        "سيتم إبلاغك برسوم الدورة والجداول وسياسات الإلغاء وقت التسجيل",
        "تُصدر الشهادات عند إكمال متطلبات البرنامج بنجاح",
        "مواد الدورة مقدمة للاستخدام الشخصي فقط ولا يجوز إعادة إنتاجها أو توزيعها",
        "نحتفظ بالحق في تعديل محتوى الدورة أو الجداول أو المدربين حسب الضرورة"
      ]
    },
    ip: {
      title: "الملكية الفكرية",
      text: "جميع المحتويات على موقعنا، بما في ذلك على سبيل المثال لا الحصر النصوص والرسومات والشعارات والصور والبرامج، هي ملك لشركة ويدو لخدمات الموارد البشرية أو مرخصيها ومحمية بموجب قوانين الملكية الفكرية الألمانية والدولية. لا يجوز لك إعادة إنتاج أو توزيع أو تعديل أو إنشاء أعمال مشتقة من محتوانا دون إذن كتابي مسبق.",
      trademarks: "اسم ويدو والشعار والعلامات ذات الصلة هي علامات تجارية لشركة ويدو لخدمات الموارد البشرية. يُحظر الاستخدام غير المصرح به لهذه العلامات."
    },
    thirdParty: {
      title: "روابط الطرف الثالث",
      text: "قد يحتوي موقعنا على روابط لمواقع أو خدمات طرف ثالث ليست مملوكة أو خاضعة لسيطرة ويدو. ليس لدينا سيطرة على ولا نتحمل أي مسؤولية عن المحتوى أو سياسات الخصوصية أو ممارسات أي مواقع طرف ثالث. زيارة هذه الروابط على مسؤوليتك الخاصة."
    },
    liability: {
      title: "تحديد المسؤولية",
      intro: "إلى أقصى حد يسمح به القانون المعمول به:",
      items: [
        "تقدم ويدو موقعها وخدماتها على أساس \"كما هي\" و\"كما هي متاحة\" دون أي ضمانات من أي نوع",
        "لا نضمن أن موقعنا سيكون متواصلاً أو خالياً من الأخطاء أو الفيروسات",
        "لن تكون ويدو مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية",
        "لن تتجاوز مسؤوليتنا الإجمالية عن أي مطالبة المبلغ الذي دفعته مقابل الخدمة المحددة التي أدت إلى المطالبة",
        "لا تنطبق هذه القيود على المسؤولية التي لا يمكن استبعادها بموجب القانون المعمول به"
      ],
      note: "لا شيء في هذه الشروط يستبعد أو يحد من مسؤوليتنا عن الوفاة أو الإصابة الشخصية الناجمة عن الإهمال أو الاحتيال أو التمثيل الاحتيالي، أو أي مسؤولية أخرى لا يمكن استبعادها بموجب القانون الألماني."
    },
    indemnification: {
      title: "التعويض",
      text: "أنت توافق على تعويض وحماية ويدو لخدمات الموارد البشرية ومسؤوليها ومديريها وموظفيها ووكلائها من أي مطالبات أو أضرار أو خسائر أو نفقات (بما في ذلك الرسوم القانونية) الناشئة عن استخدامك لموقعنا أو خدماتنا، أو انتهاكك لهذه الشروط، أو انتهاكك لأي حقوق طرف ثالث."
    },
    termination: {
      title: "الإنهاء",
      text: "نحتفظ بالحق في إنهاء أو تعليق وصولك إلى موقعنا أو خدماتنا في أي وقت، دون إشعار مسبق، لأي سبب، بما في ذلك خرق هذه الشروط. عند الإنهاء، يتوقف حقك في استخدام موقعنا فوراً."
    },
    changes: {
      title: "التغييرات على الشروط",
      text: "قد نعدل هذه الشروط والأحكام في أي وقت. ستكون التغييرات سارية فور نشرها على هذه الصفحة. يشكل استمرارك في استخدام موقعنا بعد أي تغييرات قبولاً للشروط المعدلة. نشجعك على مراجعة هذه الشروط بشكل دوري."
    },
    severability: {
      title: "قابلية الفصل",
      text: "إذا تبين أن أي حكم من هذه الشروط غير صالح أو غير قابل للتنفيذ، فستظل الأحكام المتبقية سارية المفعول بالكامل. سيتم تعديل الحكم غير الصالح إلى الحد الأدنى الضروري لجعله صالحاً وقابلاً للتنفيذ."
    },
    governing: {
      title: "القانون الحاكم والاختصاص القضائي",
      text: "تخضع هذه الشروط والأحكام وتُفسر وفقاً لقوانين جمهورية ألمانيا الاتحادية، دون اعتبار لمبادئ تنازع القوانين. تخضع أي نزاعات ناشئة عن أو تتعلق بهذه الشروط للاختصاص القضائي الحصري لمحاكم دوسلدورف، ألمانيا.",
      consumer: "إذا كنت مستهلكاً داخل الاتحاد الأوروبي، فقد يكون لديك أيضاً الحق في رفع دعوى في بلد إقامتك."
    },
    contact: {
      title: "معلومات الاتصال",
      intro: "إذا كانت لديك أي أسئلة حول هذه الشروط والأحكام، يرجى الاتصال بنا:",
      company: "ويدو لخدمات الموارد البشرية",
      address: "دوسلدورف، ألمانيا",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    }
  }
};

export const TermsConditionsPage = () => {
  const { language, direction } = useLanguage();
  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" dir={direction} data-testid="terms-conditions-page">
      <SEO
        title="Terms & Conditions"
        titleAr="الشروط والأحكام"
        description="WeDo HR Services terms and conditions. Read our terms of service, liability limitations, and user obligations for using our recruitment, training, and HR consulting services."
        descriptionAr="الشروط والأحكام لخدمات ويدو للموارد البشرية. اقرأ شروط الخدمة وحدود المسؤولية والتزامات المستخدم لاستخدام خدمات التوظيف والتدريب واستشارات الموارد البشرية."
        canonicalUrl="/terms"
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center">
              <FileText className="h-7 w-7 text-[#33AADD]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#111827]">{t.title}</h1>
              <p className="text-sm text-[#6B7280] mt-1">{t.lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.intro.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.intro.text}</p>
              </CardContent>
            </Card>

            {/* About Us */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.company.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.company.text}</p>
              </CardContent>
            </Card>

            {/* Services */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.services.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.services.intro}</p>
                <div className="space-y-4 mb-4">
                  {t.services.items.map((item, index) => (
                    <div key={index} className="bg-[#F9FAFB] rounded-lg p-4">
                      <h3 className="font-medium text-[#111827] mb-1">{item.name}</h3>
                      <p className="text-sm text-[#6B7280]">{item.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#6B7280] italic">{t.services.note}</p>
              </CardContent>
            </Card>

            {/* Website Use */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.website.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.website.intro}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280] mb-4">
                  {t.website.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-[#6B7280] italic">{t.website.disclaimer}</p>
              </CardContent>
            </Card>

            {/* Inquiries */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.inquiries.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.inquiries.text}</p>
              </CardContent>
            </Card>

            {/* Recruitment */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.recruitment.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.recruitment.intro}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280] mb-4">
                  {t.recruitment.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-[#6B7280] italic">{t.recruitment.clients}</p>
              </CardContent>
            </Card>

            {/* Training */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.training.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.training.intro}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280]">
                  {t.training.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.ip.title}</h2>
                <p className="text-[#6B7280] leading-relaxed mb-4">{t.ip.text}</p>
                <p className="text-sm text-[#6B7280] italic">{t.ip.trademarks}</p>
              </CardContent>
            </Card>

            {/* Third-Party Links */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.thirdParty.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.thirdParty.text}</p>
              </CardContent>
            </Card>

            {/* Liability */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.liability.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.liability.intro}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280] mb-4">
                  {t.liability.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-[#6B7280] italic">{t.liability.note}</p>
              </CardContent>
            </Card>

            {/* Indemnification */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.indemnification.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.indemnification.text}</p>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.termination.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.termination.text}</p>
              </CardContent>
            </Card>

            {/* Changes */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.changes.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.changes.text}</p>
              </CardContent>
            </Card>

            {/* Severability */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.severability.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.severability.text}</p>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.governing.title}</h2>
                <p className="text-[#6B7280] leading-relaxed mb-4">{t.governing.text}</p>
                <p className="text-sm text-[#6B7280] italic">{t.governing.consumer}</p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.contact.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.contact.intro}</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2">
                  <p className="font-medium text-[#111827]">{t.contact.company}</p>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <MapPin className="h-4 w-4" />
                    <span>{t.contact.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${t.contact.email}`} className="text-[#33AADD] hover:underline">{t.contact.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${t.contact.phone}`} className="text-[#33AADD] hover:underline">{t.contact.phone}</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;
